<script setup lang="ts">
import { Color } from '@previo/codebooks'
import { PCardSize, PCardRound } from '../../card/types'
import PCard from '../../card/PCard.vue'
import PPageContainer from '../../page-container/PPageContainer.vue'
import { usePrevioDS } from '../../../composables/helpers'
import SettingTreeMenu from './components/SettingTreeMenu.vue'
import DesktopMenu from './components/DesktopMenu.vue'
import MobileMenu from './components/MobileMenu.vue'
import { PSettingItem } from './PSettingItem'

type Props = {
  modules: PSettingItem[]
  currentRoute: string
}
type Emits = {
  (e: 'go-to-route', evt: Event, route: string | null): void
}
const emits = defineEmits<Emits>()
withDefaults(defineProps<Props>(), {
  modules() {
    return []
  },
})

const ds = usePrevioDS()
</script>

<template>
  <p-page-container>
    <div
      :class="[
        'ds-page-setting-wrapper',
        { 'ds-page-setting-wrapper--mobile': ds.screen().isMobile },
      ]"
    >
      <template v-if="ds.screen().isMobile">
        <div class="ds-page-setting-mobile p-flex p-flex-direction-column p-no-wrap p-full-height">
          <mobile-menu class="ds-page-setting-mobile__menu">
            <template #default>
              <setting-tree-menu
                :items="modules"
                :current-route="currentRoute"
                @go-to-route.prevent="(e, route) => emits('go-to-route', e, route)"
              />
            </template>
          </mobile-menu>

          <p-card
            :pa-mobile="PCardSize.SM"
            :color="Color.GREY_100"
            :round="PCardRound.ZERO"
            class="ds-page-setting-mobile__content"
          >
            <slot name="default" />
          </p-card>
        </div>
      </template>

      <p-card
        v-else
        :pa-mobile="PCardSize.SM"
        :color="Color.GREY_100"
        class="p-full-height p-flex p-no-wrap"
      >
        <desktop-menu>
          <template #default>
            <setting-tree-menu
              :items="modules"
              :current-route="currentRoute"
              @go-to-route.prevent="(e, route) => emits('go-to-route', e, route)"
            />
          </template>
        </desktop-menu>

        <div class="p-flex p-flex-1 p-flex-direction-column p-overflow-hidden">
          <slot name="default" />
        </div>
      </p-card>
    </div>
  </p-page-container>
</template>

<style lang="scss" scoped>
@import '../../../assets/scss/shared/mixins';

.ds-page-setting-wrapper {
  @include content-with-menu-height();
  @include safe-area-padding(bottom);
}

.ds-page-setting-mobile,
.ds-page-setting-mobile__content {
  min-height: 0;
}
</style>
